import { contactWrap, FlowTemplate, isSpacer, photoWidget, sectionIcon, singleColumnPage, type RenderContext, type Zone } from "../base";
import {
  box,
  col,
  expanded,
  hspace,
  keep,
  linkText,
  padding,
  rect,
  rich,
  row,
  sized,
  text,
  vspace,
  type Node,
  type PageSpec,
} from "../ir";
import type { RenderEntry, RenderSection, RenderSectionType } from "../model";
import { hex, WHITE } from "../theme";

const MARGIN_L = 36;
const MARGIN_R = 40;
const DATE_COL = 78;
const RAIL = 12; // date column → rail line
const RAIL_INSET = 15; // rail line → content
const LINE_W = 1.2;
const CONTENT_INDENT = DATE_COL + RAIL + LINE_W + RAIL_INSET;

const TIMELINE_TYPES = new Set<RenderSectionType>(["experience", "education", "entries", "projects"]);

/**
 * Timeline — dates in a left column and a continuous rail with a dot per
 * role, so a career reads as a story. The rail continues cleanly across page
 * breaks because every block draws its own segment. (timeline_template.dart)
 */
export class TimelineTemplate extends FlowTemplate {
  readonly info = {
    id: "timeline",
    name: "Timeline",
    tagline: "Your career as a clear visual story",
    category: "creative" as const,
    layout: "Timeline",
    defaultAccent: 0xff4f46e5,
    atsFriendly: false,
    supportsPhoto: true,
  };

  readonly fonts = {
    body: "inter" as const,
    heading: "montserrat" as const,
    display: "montserrat" as const,
    headingFollowsBody: false,
    displayFollowsBody: false,
  };

  /** Non-timeline sections (skills, languages…) sit indented past the rail. */
  zoneWidth(c: RenderContext): number {
    return c.format.width - MARGIN_L - MARGIN_R - CONTENT_INDENT;
  }

  heading(c: RenderContext, s: RenderSection): Node {
    const t = c.t;
    // Centre the marker on the rail line.
    return padding(
      { left: DATE_COL + RAIL + LINE_W / 2 - 8 },
      row([
        box({ width: 16, height: 16, bg: hex(t.accent), radius: 4, align: "center", justify: "center" }, [
          sectionIcon(c, s, WHITE, 10.5, true) ?? box({ width: 5, height: 5, bg: hex(WHITE), radius: 2.5 }),
        ]),
        hspace(RAIL_INSET - 6),
        text(s.title.toUpperCase(), t.style(t.heading.bold, 10.2, { color: t.ink, letterSpacing: 1.4, lineSpacing: 0 })),
      ])
    );
  }

  entryHeader(c: RenderContext, e: RenderEntry, s: RenderSection, z: Zone): Node {
    if (!TIMELINE_TYPES.has(s.type)) return super.entryHeader(c, e, s, z);
    const t = c.t;
    const title = e.title || e.subtitle;
    const subtitle = e.title ? e.subtitle : "";
    return col([
      linkText(title, t.style(t.heading.semiBold, 10.2, { color: t.ink, lineSpacing: 0 }), e.url),
      (subtitle || e.meta) &&
        padding(
          { top: 2 },
          rich([
            subtitle ? [subtitle, t.style(t.body.semiBold, 9.2, { color: t.accentText })] : null,
            subtitle && e.meta ? ["  ·  ", t.style(t.body.regular, 8.4, { color: t.faint })] : null,
            e.meta ? [e.meta, t.style(t.body.regular, 8.4, { color: t.muted })] : null,
          ])
        ),
    ]);
  }

  private railCell(c: RenderContext, child: Node): Node {
    return box({ pad: [0, 0, 0, RAIL_INSET], borderLeft: { width: LINE_W, color: hex(c.t.accentSoft) } }, [child]);
  }

  private timelineRow(c: RenderContext, child: Node, dates = "", dot = false): Node {
    const t = c.t;
    const rail = this.railCell(c, child);
    return row(
      [
        sized(
          DATE_COL,
          undefined,
          dates
            ? padding({ top: 1 }, text(dates, t.style(t.body.semiBold, 7.9, { color: t.accentText, lineSpacing: 1 }), "right"))
            : null
        ),
        hspace(RAIL),
        expanded(
          dot
            ? box({ position: "relative" }, [
                rail,
                box({
                  position: "absolute",
                  left: -(9 - LINE_W) / 2 - 0.1,
                  top: 1.5,
                  width: 9,
                  height: 9,
                  radius: 4.5,
                  bg: hex(WHITE),
                  border: { width: 2.2, color: hex(t.accent) },
                }),
              ])
            : rail
        ),
      ],
      { align: "start" }
    );
  }

  sectionBlocks(c: RenderContext, s: RenderSection, z: Zone = "main"): Node[] {
    const t = c.t;
    const head = this.heading(c, s);
    if (!TIMELINE_TYPES.has(s.type)) {
      const content = this.contentBlocks(c, s, z).map((b) => padding({ left: CONTENT_INDENT }, b));
      return [keep([head, vspace(this.headingGap(c, z)), content[0]]), ...content.slice(1), vspace(this.sectionGap(c, z))];
    }

    // Timeline sections: one rail row per block.
    const blocks: Node[] = [];
    s.entries.forEach((e, i) => {
      const entry = this.entryBlocks(c, e, s, z).filter((n) => !isSpacer(n));
      if (!entry.length) return;
      blocks.push(this.timelineRow(c, entry[0], e.dates, true));
      for (const b of entry.slice(1)) blocks.push(this.timelineRow(c, b));
      if (i < s.entries.length - 1) blocks.push(this.timelineRow(c, vspace(this.entryGap(c, z))));
    });
    return [
      keep([head, vspace(this.headingGap(c, z)), blocks[0]]),
      ...blocks.slice(1),
      vspace(this.sectionGap(c, z) + t.gap(2)),
    ];
  }

  build(c: RenderContext): PageSpec {
    const t = c.t;
    const m = c.model;
    const photo = photoWidget(c, 68, { ring: t.accentSoft, ringWidth: 2 });
    const header = col([
      row([
        photo,
        photo && hspace(16),
        expanded(
          col([
            m.name && text(m.name, t.style(t.display.bold, 24, { color: t.ink, lineSpacing: 0 })),
            m.headline && vspace(4),
            m.headline &&
              text(m.headline.toUpperCase(), t.style(t.heading.semiBold, 9.4, { color: t.accentText, letterSpacing: 1.4, lineSpacing: 1 })),
            m.contacts.length > 0 && vspace(9),
            m.contacts.length > 0 &&
              contactWrap(c, { style: t.style(t.body.regular, 8.3, { color: t.muted, lineSpacing: 0 }), iconColor: t.accent }),
          ])
        ),
      ]),
      vspace(14),
      row([rect({ width: 46, height: 2.4, color: t.accent }), expanded(rect({ height: 0.7, color: t.line }))]),
      vspace(t.gap(18)),
    ]);
    return singleColumnPage(c, [MARGIN_L, 38, MARGIN_R, 32], [keep([header]), ...this.allSections(c, m.sections)]);
  }
}
